import { useClerk } from '@clerk/clerk-react';
import { Link, useLocation } from '@tanstack/react-router';
import { useAtom, useAtomValue } from 'jotai/index';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useRef, useState, useMemo, useEffect } from 'react';
import { globalSearchRefAtom } from '~/atoms/globalSearch';
import {
  agentChatVisibleAtom,
  currentDashboardIdAtom,
} from '~/atoms/dashboardAtom';
import { TruncatedTextWithTooltip } from './TruncatedTextWithTooltip';
import { AddEditDashboardDialog } from '~/components/ui/dashboard/AddEditDashboardDialog';
import {
  DeleteDashboardDialog,
  showDeleteConfirmation,
} from '~/components/ui/dashboard/DeleteDashboardDialog';
import { GlobalSearchPopupRef } from '~/components/ui/globalSearch/GlobalSearchPopup';
import {
  useDashboards,
  useCreateDashboard,
  useDeleteDashboard,
} from '~/hooks/useDashboard';
import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';
import { useNavigate } from '@tanstack/react-router';
import { useRBAC } from '~/hooks/useRBAC';
import { StreamChat } from '~/components/ui/misc/StreamChat/StreamChat';
import '~/styles/component/bq-sidebar-v2.css';

// Custom hook to fetch app version
const useAppVersion = () => {
  const [version, setVersion] = useState<string>('');

  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then((data) => setVersion(data.version))
      .catch(() => setVersion('unknown'));
  }, []);

  return version;
};

interface Dashboard {
  id: string;
  name: string;
  config: Config[];
  icon?: string;
  isDefault?: boolean;
  portfolioId?: string;
}

export const SidebarStatic = ({
  isExpanded = false,
  onExpandToggle,
  sidebarWidth: externalSidebarWidth = 240,
  onSidebarWidthChange,
}: {
  isExpanded: boolean;
  onExpandToggle: (expanded: boolean) => void;
  sidebarWidth?: number;
  onSidebarWidthChange?: (width: number) => void;
}) => {
  const { signOut } = useClerk();
  const { data: dashboardsResponse } = useDashboards();
  const createDashboard = useCreateDashboard();
  const deleteDashboard = useDeleteDashboard();
  const [showAddDashboardDialog, setShowAddDashboardDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(
    null,
  );
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, canCreate, canEdit, canDelete } = useRBAC();
  const appVersion = useAppVersion();

  const [localSidebarWidth, setLocalSidebarWidth] =
    useState(externalSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [showChat, setShowChat] = useAtom(agentChatVisibleAtom);
  const [chatWidth, setChatWidth] = useState(400);
  const [isChatResizing, setIsChatResizing] = useState(false);
  const isChatResizingRef = useRef(false);
  const dashboardId = useAtomValue(currentDashboardIdAtom);

  // Sync external width changes
  useEffect(() => {
    setLocalSidebarWidth(externalSidebarWidth);
  }, [externalSidebarWidth]);

  const sidebarWidth = localSidebarWidth;

  const [globalSearch] = useAtom<GlobalSearchPopupRef | null>(
    globalSearchRefAtom,
  );

  const dashboards = useMemo(
    () => (Array.isArray(dashboardsResponse) ? dashboardsResponse : []),
    [dashboardsResponse],
  );

  const handleEditDashboard = (dashboard: Dashboard) => {
    setDialogMode('edit');
    setSelectedDashboard(dashboard);
    setShowAddDashboardDialog(true);
  };

  const handleCreateDashboard = () => {
    setDialogMode('create');
    setSelectedDashboard(null);
    setShowAddDashboardDialog(true);
  };

  const handleDashboardSubmit = async (data: {
    name: string;
    icon?: string | null;
    id?: string;
    isDefault: boolean;
    selectedPortfolioId?: string;
    useDefaultLayout?: boolean;
  }): Promise<void> => {
    return new Promise((resolve, reject) => {
      const payload: {
        id?: string;
        name: string;
        config: Config[];
        isDefault: boolean;
        portfolioId?: string;
        icon?: string;
      } = {
        id: data.id,
        name: data.name,
        config: data.useDefaultLayout
          ? DEFAULT_DASHBOARD_LAYOUT_SL
          : selectedDashboard?.config || [],
        isDefault: data.isDefault,
        portfolioId: data.selectedPortfolioId,
      };

      // Explicitly include icon field even if null
      if (data.icon !== undefined) {
        payload.icon = data.icon === null ? undefined : data.icon;
      }

      createDashboard.mutate(payload, {
        onSuccess: () => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail:
              dialogMode === 'create'
                ? 'Dashboard created successfully'
                : 'Dashboard updated successfully',
          });
          setShowAddDashboardDialog(false);
          resolve();
        },
        onError: () => {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail:
              dialogMode === 'create'
                ? 'Failed to create dashboard'
                : 'Failed to update dashboard',
          });
          reject(new Error('Dashboard operation failed'));
        },
      });
    });
  };

  const handleChatMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsChatResizing(true);
    isChatResizingRef.current = true;

    const startX = e.clientX;
    const startWidth = chatWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isChatResizingRef.current) return;

      const deltaX = e.clientX - startX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));

      setChatWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsChatResizing(false);
      isChatResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);
  };

  const handleDeleteDashboard = (dashboard: Dashboard) => {
    showDeleteConfirmation(dashboard, (id) => {
      deleteDashboard.mutate(id, {
        onSuccess: () => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Dashboard deleted successfully',
          });
          // Navigate to default dashboard if available, otherwise first dashboard
          const remainingDashboards = dashboards.filter((d) => d.id !== id);
          if (remainingDashboards.length > 0) {
            const defaultDashboard = remainingDashboards.find(
              (d) => d.isDefault,
            );
            const targetDashboard = defaultDashboard || remainingDashboards[0];
            navigate({
              to: '/dashboard/$dashboardId',
              params: { dashboardId: targetDashboard.id },
            });
          }
        },
        onError: () => {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete dashboard',
          });
        },
      });
    });
  };

  const isDashboardActive = (dashboardId: string) => {
    return location.pathname === `/dashboard/${dashboardId}`;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      // Set min width to 56px (collapsed) and max to 400px
      if (newWidth >= 56 && newWidth <= 400) {
        setLocalSidebarWidth(newWidth);
        onSidebarWidthChange?.(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onSidebarWidthChange]);

  return (
    <div className="sidebar-fixed">
      <Toast ref={toast} />
      <div
        ref={sidebarRef}
        className={`sidebar-static ${isExpanded ? 'expanded' : ''}`}
        style={{
          width: isExpanded ? `${sidebarWidth}px` : '56px',
        }}
      >
        <div
          className="sidebar-content"
          style={{
            width: isExpanded ? `calc(${sidebarWidth}px - 1px)` : '55px',
          }}
        >
          {/* Header with Logo */}
          <div
            className="sidebar-header"
            style={{
              padding: isExpanded ? '16px' : '8px',
              borderBottom: isExpanded
                ? '1px solid var(--bq-border-muted, var(--surface-border))'
                : 'none',
              justifyContent: isExpanded ? 'space-between' : 'center',
            }}
          >
            {isExpanded ? (
              <>
                <div>
                  <div className="flex align-items-center">
                    <img
                      src="/logo.svg"
                      alt="Banqora"
                      style={{
                        width: '21px',
                        height: '21px',
                        marginRight: '8px',
                      }}
                    />
                    <span
                      className="font-bold"
                      style={{
                        fontSize: '18px',
                        color: 'var(--bq-text-primary, var(--text-color))',
                      }}
                    >
                      Visible Alpha
                    </span>
                  </div>
                  <p
                    className="m-0 mt-1"
                    style={{
                      fontSize: '12px',
                      color:
                        'var(--bq-text-secondary, var(--text-color-secondary))',
                      paddingLeft: '32px',
                    }}
                  >
                    Powered by Banqora
                  </p>
                </div>
                <Button
                  icon="pi pi-angle-double-left"
                  className="color-text-inverted"
                  style={{
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    color: 'var(--bq-text-primary, var(--text-color))',
                  }}
                  onClick={() => onExpandToggle(false)}
                  text
                  aria-label="collapse"
                />
              </>
            ) : (
              <img
                src="/logo.svg"
                alt="Banqora"
                style={{ width: '24px', height: '24px' }}
              />
            )}
          </div>

          {/* Expand button (collapsed state only) */}
          {!isExpanded && (
            <div className="expand-btn-container">
              <Button
                icon="pi pi-angle-double-right"
                className="color-text-inverted"
                style={{
                  background: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  width: '40px',
                  height: '40px',
                  color: 'var(--bq-text-primary, var(--text-color))',
                }}
                onClick={() => onExpandToggle(true)}
                text
                aria-label="expand"
              />
            </div>
          )}

          {/* Main Content */}
          <div
            style={{ flex: '0 0 auto', paddingTop: '8px', overflowY: 'auto' }}
          >
            {/* Search Section */}
            <div className="sidebar-section" style={{ marginBottom: '16px' }}>
              {isExpanded && <div className="section-header">Search</div>}
              <Tooltip
                target=".search"
                content="Search"
                position="right"
                disabled={isExpanded}
                pt={{
                  root: {
                    style: {
                      zIndex: 9999,
                    },
                  },
                }}
              />
              <div
                className={`search nav-item ${isExpanded ? 'expanded' : ''}`}
                onClick={() => {
                  if (globalSearch) {
                    globalSearch.show();
                  }
                }}
                style={{
                  justifyContent: isExpanded ? 'flex-start' : 'center',
                  cursor: 'pointer',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20px"
                  viewBox="0 -960 960 960"
                  width="20px"
                  fill="currentColor"
                  style={{
                    marginRight: isExpanded ? '8px' : 0,
                  }}
                >
                  <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                </svg>
                {isExpanded && <span>Search</span>}
              </div>
            </div>

            {/* Dashboards Section */}
            {hasAccess('dashboard') && (
              <div className="sidebar-section" style={{ marginBottom: '16px' }}>
                {isExpanded && <div className="section-header">Dashboards</div>}

                {canCreate('dashboard') && (
                  <>
                    <Tooltip
                      target=".add-dashboard-btn"
                      content="Add Dashboard"
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <div
                      style={{
                        padding: '8px 16px',
                        borderBottom: isExpanded
                          ? '1px solid var(--bq-border-muted)'
                          : 'none',
                      }}
                    >
                      <Button
                        className="add-dashboard-btn"
                        icon="pi pi-plus"
                        label={isExpanded ? 'Add Dashboard' : ''}
                        size="small"
                        outlined
                        onClick={() => handleCreateDashboard()}
                        style={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          padding: '0.5rem 0.75rem',
                        }}
                      />
                    </div>
                  </>
                )}

                {/* Navigation Links Section */}
                <div
                  className="sidebar-section"
                  style={{ marginBottom: '16px' }}
                >
                  {isExpanded && (
                    <div className="section-header">Navigation</div>
                  )}

                  {hasAccess('fund') && (
                    <>
                      <Tooltip
                        target=".nav-fund-overview"
                        content="Fund Overview"
                        position="right"
                        disabled={isExpanded}
                        pt={{
                          root: {
                            style: {
                              zIndex: 9999,
                            },
                          },
                        }}
                      />
                      <Link
                        to="/fund/overview"
                        className={`nav-fund-overview nav-item ${
                          location.pathname === '/fund/overview'
                            ? 'active'
                            : 'inactive'
                        }`}
                        style={{
                          justifyContent: isExpanded ? 'flex-start' : 'center',
                        }}
                        activeProps={{
                          className: 'active',
                        }}
                        search={{ fundId: undefined }}
                      >
                        <i
                          className="pi pi-chart-bar"
                          style={{
                            fontSize: '20px',
                            marginRight: isExpanded ? '8px' : 0,
                          }}
                        />
                        {isExpanded && <span>Fund Overview</span>}
                      </Link>
                    </>
                  )}

                  {hasAccess('portfolio') && (
                    <>
                      <Tooltip
                        target=".nav-portfolio-companies"
                        content="Portfolio companies"
                        position="right"
                        disabled={isExpanded}
                        pt={{
                          root: {
                            style: {
                              zIndex: 9999,
                            },
                          },
                        }}
                      />
                      <Link
                        to="/portfolios"
                        className={`nav-portfolio-companies nav-item ${
                          location.pathname === '/portfolios'
                            ? 'active'
                            : 'inactive'
                        }`}
                        style={{
                          justifyContent: isExpanded ? 'flex-start' : 'center',
                        }}
                        activeProps={{
                          className: 'active',
                        }}
                        search={{}}
                      >
                        <i
                          className="pi pi-building"
                          style={{
                            fontSize: '20px',
                            marginRight: isExpanded ? '8px' : 0,
                          }}
                        />
                        {isExpanded && <span>Portfolio companies</span>}
                      </Link>
                    </>
                  )}

                  {hasAccess('document') && (
                    <>
                      <Tooltip
                        target=".nav-documents"
                        content="Documents"
                        position="right"
                        disabled={isExpanded}
                        pt={{
                          root: {
                            style: {
                              zIndex: 9999,
                            },
                          },
                        }}
                      />
                      <Link
                        to="/documents"
                        className={`nav-documents nav-item ${
                          location.pathname === '/documents'
                            ? 'active'
                            : 'inactive'
                        }`}
                        style={{
                          justifyContent: isExpanded ? 'flex-start' : 'center',
                        }}
                        activeProps={{
                          className: 'active',
                        }}
                        search={{}}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 -960 960 960"
                          fill="currentColor"
                          style={{
                            marginRight: isExpanded ? '8px' : 0,
                          }}
                        >
                          <path d="M140-160q-24 0-42-18.5T80-220v-520q0-23 18-41.5t42-18.5h281l60 60h339q23 0 41.5 18.5T880-680v460q0 23-18.5 41.5T820-160H140Zm0-60h680v-460H456l-60-60H140v520Zm0 0v-520 520Z"></path>
                        </svg>
                        {isExpanded && <span>Documents</span>}
                      </Link>
                    </>
                  )}

                  {hasAccess('workflow') && (
                    <>
                      <Tooltip
                        target=".nav-workflows"
                        content="Workflows"
                        position="right"
                        disabled={isExpanded}
                        pt={{
                          root: {
                            style: {
                              zIndex: 9999,
                            },
                          },
                        }}
                      />
                      <Link
                        to="/workflows"
                        className={`nav-workflows nav-item ${
                          location.pathname === '/workflows'
                            ? 'active'
                            : 'inactive'
                        }`}
                        style={{
                          justifyContent: isExpanded ? 'flex-start' : 'center',
                        }}
                        activeProps={{
                          className: 'active',
                        }}
                        search={{}}
                      >
                        <i
                          className="pi pi-inbox"
                          style={{
                            fontSize: '20px',
                            marginRight: isExpanded ? '8px' : 0,
                          }}
                        />
                        {isExpanded && <span>Workflows</span>}
                      </Link>
                    </>
                  )}
                </div>

                {dashboards.map((dashboard: Dashboard) => (
                  <div key={dashboard.id}>
                    <Tooltip
                      target={`.dashboard-link-${dashboard.id}`}
                      content={dashboard.name}
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <div
                      className={`dashboard-link-${dashboard.id} dashboard-item ${
                        isDashboardActive(dashboard.id) ? 'active' : ''
                      }`}
                    >
                      <Link
                        to="/dashboard/$dashboardId"
                        params={{ dashboardId: dashboard.id }}
                        activeProps={{
                          className: 'active',
                        }}
                        search={{}}
                        aria-label={
                          dashboard.icon
                            ? isExpanded
                              ? dashboard.name
                              : dashboard.name
                            : (() => {
                                const firstTwo = dashboard.name
                                  .slice(0, 2)
                                  .toUpperCase();
                                const words = dashboard.name.split(' ');
                                const firstLast =
                                  `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
                                const hasFirstTwoDuplicate = dashboards.some(
                                  (d) =>
                                    d.id !== dashboard.id &&
                                    d.name.slice(0, 2).toUpperCase() ===
                                      firstTwo,
                                );
                                const hasFirstLastDuplicate = dashboards.some(
                                  (d) =>
                                    d.id !== dashboard.id &&
                                    `${d.name.split(' ')[0][0]}${
                                      d.name.split(' ').slice(-1)[0][0]
                                    }`.toUpperCase() === firstLast,
                                );
                                const initials = !hasFirstTwoDuplicate
                                  ? firstTwo
                                  : !hasFirstLastDuplicate
                                    ? firstLast
                                    : `${dashboard.name[0]}${dashboard.name[dashboard.name.length - 1]}`.toUpperCase();
                                return isExpanded
                                  ? `${initials} ${dashboard.name}`
                                  : initials;
                              })()
                        }
                        style={{
                          flex: 1,
                          minWidth: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: isDashboardActive(dashboard.id)
                            ? 600
                            : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          gap: '8px',
                        }}
                      >
                        {dashboard.icon ? (
                          <i
                            className={dashboard.icon}
                            style={{
                              fontSize: '20px',
                              marginRight: isExpanded ? '8px' : 0,
                            }}
                          />
                        ) : (
                          <>
                            <Avatar
                              label={(() => {
                                const firstTwo = dashboard.name
                                  .slice(0, 2)
                                  .toUpperCase();
                                const words = dashboard.name.split(' ');
                                const firstLast =
                                  `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();

                                // Check if any other dashboard has the same first two letters
                                const hasFirstTwoDuplicate = dashboards.some(
                                  (d) =>
                                    d.id !== dashboard.id &&
                                    d.name.slice(0, 2).toUpperCase() ===
                                      firstTwo,
                                );

                                // Check if any other dashboard has the same first-last initials
                                const hasFirstLastDuplicate = dashboards.some(
                                  (d) =>
                                    d.id !== dashboard.id &&
                                    `${d.name.split(' ')[0][0]}${
                                      d.name.split(' ').slice(-1)[0][0]
                                    }`.toUpperCase() === firstLast,
                                );

                                // If first two letters are unique, use them
                                if (!hasFirstTwoDuplicate) return firstTwo;

                                // If first-last initials are unique, use them
                                if (!hasFirstLastDuplicate) return firstLast;

                                // If both are duplicates, use first letter and last letter of the name
                                return `${dashboard.name[0]}${dashboard.name[dashboard.name.length - 1]}`.toUpperCase();
                              })()}
                              style={{
                                backgroundColor: isDashboardActive(dashboard.id)
                                  ? 'var(--bq-color-black)'
                                  : 'var(--bq-color-gray-lighter)',
                                color: isDashboardActive(dashboard.id)
                                  ? 'var(--bq-color-white)'
                                  : 'var(--bq-color-black)',
                                fontSize: '12px',
                                width: '24px',
                                height: '24px',
                                marginRight: isExpanded ? '8px' : 0,
                              }}
                            />
                            {!isExpanded && (
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '-9999px',
                                }}
                              >
                                {dashboard.name}
                              </span>
                            )}
                          </>
                        )}
                        {isExpanded && (
                          <>
                            {!dashboard.icon && (
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '-9999px',
                                }}
                              >
                                {(() => {
                                  const firstTwo = dashboard.name
                                    .slice(0, 2)
                                    .toUpperCase();
                                  const words = dashboard.name.split(' ');
                                  const firstLast =
                                    `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
                                  const hasFirstTwoDuplicate = dashboards.some(
                                    (d) =>
                                      d.id !== dashboard.id &&
                                      d.name.slice(0, 2).toUpperCase() ===
                                        firstTwo,
                                  );
                                  const hasFirstLastDuplicate = dashboards.some(
                                    (d) =>
                                      d.id !== dashboard.id &&
                                      `${d.name.split(' ')[0][0]}${
                                        d.name.split(' ').slice(-1)[0][0]
                                      }`.toUpperCase() === firstLast,
                                  );
                                  return !hasFirstTwoDuplicate
                                    ? firstTwo
                                    : !hasFirstLastDuplicate
                                      ? firstLast
                                      : `${dashboard.name[0]}${dashboard.name[dashboard.name.length - 1]}`.toUpperCase();
                                })()}{' '}
                              </span>
                            )}
                            <TruncatedTextWithTooltip
                              text={dashboard.name}
                              className={`dashboard-name-${dashboard.id} name`}
                              dashboardId={dashboard.id}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '14px',
                              }}
                            >
                              {dashboard.name}
                            </TruncatedTextWithTooltip>
                            <div
                              className="dashboard-actions"
                              style={{ display: 'none' }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {canEdit('dashboard') && (
                                <span
                                  className="pi pi-pencil"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditDashboard(dashboard);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    color:
                                      'var(--bq-text-secondary, var(--text-color-secondary))',
                                    fontSize: '14px',
                                    padding: '4px',
                                  }}
                                />
                              )}
                              {dashboards.length > 1 &&
                                canDelete('dashboard') && (
                                  <span
                                    className="pi pi-trash"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteDashboard(dashboard);
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      color:
                                        'var(--bq-text-secondary, var(--text-color-secondary))',
                                      fontSize: '14px',
                                      padding: '4px',
                                    }}
                                  />
                                )}
                            </div>
                          </>
                        )}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Agent Chat */}
            {!location.pathname.startsWith('/popout/') && (
              <div className="sidebar-section" style={{ marginBottom: '16px' }}>
                <Tooltip
                  target=".nav-agent-chat"
                  content="Agent Chat"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <Button
                  className={`nav-agent-chat ${isExpanded ? 'expanded' : ''} p-button-primary`}
                  icon="pi pi-comments"
                  label={isExpanded ? 'Agent Chat' : undefined}
                  onClick={() => setShowChat(!showChat)}
                  aria-label="Agent Chat"
                  style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                    margin: isExpanded ? '0 18px' : '0 auto',
                    padding: isExpanded ? '0 50px' : '',
                    width: isExpanded ? 'auto' : '40px',
                  }}
                />
              </div>
            )}

            {/* Settings Section */}
            {!location.pathname.startsWith('/popout/') && (
              <div className="sidebar-section" style={{ marginBottom: '16px' }}>
                {hasAccess('setting') && (
                  <>
                    <Tooltip
                      target=".nav-settings"
                      content="Settings"
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <Link
                      to="/settings"
                      className={`nav-settings nav-item ${
                        location.pathname === '/settings'
                          ? 'active'
                          : 'inactive'
                      }`}
                      style={{
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                      }}
                      activeProps={{
                        className: 'active',
                      }}
                      activeOptions={{ exact: true }}
                      search={{}}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 -960 960 960"
                        fill="currentColor"
                        style={{
                          marginRight: isExpanded ? '8px' : 0,
                        }}
                      >
                        <path d="m388-80-20-126q-19-7-40-19t-37-25l-118 54-93-164 108-79q-2-9-2.5-20.5T185-480q0-9 .5-20.5T188-521L80-600l93-164 118 54q16-13 37-25t40-18l20-127h184l20 126q19 7 40.5 18.5T669-710l118-54 93 164-108 77q2 10 2.5 21.5t.5 21.5q0 10-.5 21t-2.5 21l108 78-93 164-118-54q-16 13-36.5 25.5T592-206L572-80H388Zm48-60h88l14-112q33-8 62.5-25t53.5-41l106 46 40-72-94-69q4-17 6.5-33.5T715-480q0-17-2-33.5t-7-33.5l94-69-40-72-106 46q-23-26-52-43.5T538-708l-14-112h-88l-14 112q-34 7-63.5 24T306-642l-106-46-40 72 94 69q-4 17-6.5 33.5T245-480q0 17 2.5 33.5T254-413l-94 69 40 72 106-46q24 24 53.5 41t62.5 25l14 112Zm44-210q54 0 92-38t38-92q0-54-38-92t-92-38q-54 0-92 38t-38 92q0 54 38 92t92 38Zm0-130Z"></path>
                      </svg>
                      {isExpanded && <span>Settings</span>}
                    </Link>
                  </>
                )}
                {hasAccess('user') && (
                  <>
                    <Tooltip
                      target=".nav-profile"
                      content="Profile"
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <Link
                      to="/user"
                      className={`nav-profile nav-item ${
                        location.pathname === '/user' ? 'active' : 'inactive'
                      }`}
                      style={{
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                      }}
                      activeProps={{
                        className: 'active',
                      }}
                      activeOptions={{ exact: true }}
                      search={{}}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 -960 960 960"
                        fill="currentColor"
                        style={{
                          marginRight: isExpanded ? '8px' : 0,
                        }}
                      >
                        <path d="M480-481q-66 0-108-42t-42-108q0-66 42-108t108-42q66 0 108 42t42 108q0 66-42 108t-108 42ZM160-160v-94q0-38 19-65t49-41q67-30 128.5-45T480-420q62 0 123 15.5t127.92 44.69q31.3 14.13 50.19 40.97Q800-292 800-254v94H160Zm60-60h520v-34q0-16-9.5-30.5T707-306q-64-31-117-42.5T480-360q-57 0-111 11.5T252-306q-14 7-23 21.5t-9 30.5v34Zm260-321q39 0 64.5-25.5T570-631q0-39-25.5-64.5T480-721q-39 0-64.5 25.5T390-631q0 39 25.5 64.5T480-541Zm0-90Zm0 411Z"></path>
                      </svg>
                      {isExpanded && <span>Profile</span>}
                    </Link>
                  </>
                )}
                <Tooltip
                  target=".nav-exit"
                  content="Exit"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <a
                  className={`nav-exit nav-item ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => {
                    signOut({
                      redirectUrl: process.env.REACT_CLERK_SIGN_IN_URL,
                    });
                  }}
                  style={{
                    cursor: 'pointer',
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 -960 960 960"
                    fill="currentColor"
                    style={{
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  >
                    <path d="M180-120q-24 0-42-18t-18-42v-600q0-24 18-42t42-18h299v60H180v600h299v60H180Zm486-185-43-43 102-102H360v-60h363L621-612l43-43 176 176-174 174Z"></path>
                  </svg>
                  {isExpanded && <span>Exit</span>}
                </a>
              </div>
            )}
          </div>

          {/* Version display at bottom */}
          {isExpanded && appVersion && (
            <div
              className="version-display"
              style={{
                marginTop: 'auto',
                padding: '16px',
                borderTop:
                  '1px solid var(--bq-border-muted, var(--surface-border))',
                fontSize: '12px',
                color: 'var(--bq-text-secondary, var(--text-color-secondary))',
                textAlign: 'center',
              }}
            >
              v{appVersion}
            </div>
          )}
        </div>

        {/* Resize handle */}
        {isExpanded && (
          <div
            onMouseDown={handleMouseDown}
            style={{
              position: 'absolute',
              top: 0,
              right: '-4px',
              width: '8px',
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: 'transparent',
              zIndex: 2,
            }}
          />
        )}
      </div>

      {/* Chat Panel - shows as overlay when opened */}
      {showChat && (
        <div
          style={{
            position: 'fixed',
            left: isExpanded ? `${sidebarWidth}px` : '56px',
            top: 0,
            width: `${chatWidth}px`,
            height: '100vh',
            backgroundColor: 'var(--bq-surface-card, var(--surface-card))',
            borderRight:
              '1px solid var(--bq-border-muted, var(--surface-border))',
            zIndex: 1000,
            boxShadow: '2px 0 8px var(--bq-shadow-color, rgba(0,0,0,0.1))',
          }}
        >
          <StreamChat
            initialMessage="If you have any specific questions or need further analysis, feel free to ask!"
            onExpandToggle={() => setShowChat(false)}
            dashboardId={dashboardId ?? undefined}
          />

          {/* Right-side resize handle for chat panel */}
          <div
            onMouseDown={handleChatMouseDown}
            style={{
              position: 'absolute',
              top: 0,
              right: '-2px',
              width: '8px',
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: isChatResizing
                ? 'var(--bq-surface-muted, var(--surface-hover))'
                : 'transparent',
              zIndex: 10,
              borderLeft: isChatResizing
                ? '2px solid var(--bq-color-primary)'
                : '2px solid var(--bq-border-muted, var(--surface-border))',
              boxShadow: isChatResizing
                ? '0 0 8px var(--bq-color-primary)'
                : 'none',
            }}
            title="Drag to resize chat panel width"
          />
        </div>
      )}

      <AddEditDashboardDialog
        visible={showAddDashboardDialog}
        onHide={() => {
          setShowAddDashboardDialog(false);
          setSelectedDashboard(null);
        }}
        onSubmit={handleDashboardSubmit}
        mode={dialogMode}
        initialData={
          selectedDashboard
            ? {
                name: selectedDashboard.name,
                icon: selectedDashboard.icon,
                id: selectedDashboard.id,
                isDefault: selectedDashboard.isDefault ?? false,
                portfolioId: selectedDashboard.portfolioId,
              }
            : undefined
        }
      />
      <DeleteDashboardDialog />
    </div>
  );
};
